import type { CreateOcurrenceDto } from "@/dtos/CreateOcurrenceDto";
import type { ReopenOcurrenceDto } from "@/dtos/ReopenOcurrenceDto";
import type { ReopenEligibility } from "@/types/ReopenEligibility";
import type { MediaDto } from "@/dtos/MediaDto";
import api from "@/services/api";
import {
  useMutation,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const useGetCategories = () => {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await api.get("/categories");
      return response.data;
    },
  });
};

export const useCreateOcurrence = () => {
  return useMutation({
    mutationKey: ["createOcurrence"],
    mutationFn: async (createOcurrenceDto: CreateOcurrenceDto) => {
      const response = await api.post("/occurrences", createOcurrenceDto);
      return response.data;
    },
  });
};

export const useCreateAnonymousOcurrence = () => {
  return useMutation({
    mutationKey: ["createAnonymousOcurrence"],
    mutationFn: async (createOcurrenceDto: CreateOcurrenceDto) => {
      const response = await api.post("/occurrences/anonymous", createOcurrenceDto);
      return response.data;
    },
  });
};

export const useGetOcurrences = (filters?: {
  status?: string[];
  protocol?: string;
  includeFollowing?: boolean;
}) => {
  return useQuery({
    queryKey: ["getOcurrences", filters],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters?.status && filters.status.length > 0) {
        filters.status.forEach((status) => {
          params.append("status", status);
        });
      }

      if (filters?.protocol && filters.protocol.trim()) {
        params.append("protocol", filters.protocol.trim());
      }

      if (filters?.includeFollowing) {
        params.append("includeFollowing", "true");
      }

      // Ordenação padrão: updatedAt desc (mais recente primeiro)
      params.append("orderBy", "updatedAt");
      params.append("order", "desc");

      const response = await api.get(`/occurrences?${params.toString()}`);
      return response.data;
    },
  });
};

export const useInfiniteOcurrences = (filters?: {
  status?: string[];
  search?: string;
  includeFollowing?: boolean;
}) => {
  return useInfiniteQuery({
    queryKey: ["infiniteOcurrences", filters?.status, filters?.search, filters?.includeFollowing],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams();

      if (filters?.status && filters.status.length > 0) {
        filters.status.forEach((status) => {
          params.append("status", status);
        });
      }

      if (filters?.search && filters.search.trim()) {
        params.append("search", filters.search.trim());
      }

      if (filters?.includeFollowing) {
        params.append("includeFollowing", "true");
      }

      // Ordenação padrão: updatedAt desc (mais recente primeiro)
      params.append("orderBy", "updatedAt");
      params.append("order", "desc");
      params.append("page", pageParam.toString());
      params.append("limit", "20");

      const response = await api.get(`/occurrences?${params.toString()}`);
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined;
    },
    initialPageParam: 1,
  });
};

export const useGetOcurrenceById = (
  id: string,
  options?: { initialData?: any },
) => {
  return useQuery({
    queryKey: ["getOcurrence", id],
    queryFn: async () => {
      const response = await api.get(`/occurrences/${id}`);
      return response.data;
    },
    enabled: !!id,
    initialData: options?.initialData,
  });
};

export const useReopenOcurrence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      occurrenceId,
      reason,
      description,
    }: {
      occurrenceId: string;
      reason: string;
      description: string;
    }) => {
      const response = await api.post(`/occurrences/${occurrenceId}/reopen`, {
        reason,
        description,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate occurrence queries
      queryClient.invalidateQueries({
        queryKey: ["getOcurrence", variables.occurrenceId],
      });
      queryClient.invalidateQueries({ queryKey: ["getOcurrences"] });
      queryClient.invalidateQueries({ queryKey: ["infiniteOcurrences"] });
      queryClient.invalidateQueries({
        queryKey: ["canReopen", variables.occurrenceId],
      });
    },
  });
};

export const useUpdateOccurrenceMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      occurrenceId,
      media,
    }: {
      occurrenceId: string;
      media: MediaDto[];
    }) => {
      const response = await api.patch(`/occurrences/${occurrenceId}`, {
        media,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate occurrence queries
      queryClient.invalidateQueries({
        queryKey: ["getOcurrence", variables.occurrenceId],
      });
      queryClient.invalidateQueries({ queryKey: ["getOcurrences"] });
      queryClient.invalidateQueries({ queryKey: ["infiniteOcurrences"] });
    },
  });
};

export const useCanReopenOcurrence = (occurrenceId: string) => {
  return useQuery<ReopenEligibility>({
    queryKey: ["canReopen", occurrenceId],
    queryFn: async () => {
      const response = await api.get(`/occurrences/${occurrenceId}/can-reopen`);
      return response.data;
    },
    enabled: !!occurrenceId,
  });
};
